import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Trash2, AlertTriangle, MessageSquare, Users, BarChart3, RotateCcw } from "lucide-react";
import { SettingsCard } from "./SettingsCard";
import { Trans, useTranslation } from "react-i18next";
import i18n from "@/i18n";

interface ResetAction {
  id: string;
  /** Fallback em pt-BR; a chave vence quando presente. */
  label: string;
  labelKey: string;
  description: string;
  descriptionKey: string;
  icon: React.ReactNode;
  table?: string;
  color: string;
}

const resetActions: ResetAction[] = [
  {
    id: "messages",
    labelKey: "lists.resetMessages",
    descriptionKey: "lists.resetMessagesDesc",
    label: "Todas as Mensagens",
    description: "Apagar todas as mensagens de conversas do WhatsApp (account_messages)",
    icon: <MessageSquare className="w-5 h-5" />,
    table: "account_messages",
    color: "orange",
  },
  {
    id: "crm",
    labelKey: "lists.resetCrm",
    descriptionKey: "lists.resetCrmDesc",
    label: "Todos os Dados do CRM",
    description: "Apagar todas as oportunidades e dados do pipeline (account_opportunities)",
    icon: <BarChart3 className="w-5 h-5" />,
    table: "account_opportunities",
    color: "red",
  },
  {
    id: "contacts",
    labelKey: "lists.resetContacts",
    descriptionKey: "lists.resetContactsDesc",
    label: "Todos os Contatos",
    description: "Apagar todos os contatos salvos (account_contacts)",
    icon: <Users className="w-5 h-5" />,
    table: "account_contacts",
    color: "red",
  },
  {
    id: "account",
    labelKey: "lists.resetAccount",
    descriptionKey: "lists.resetAccountDesc",
    label: "Zerar conta",
    description:
      "Apagar tudo da conta: mensagens, CRM, contatos, assistentes, pipelines, campanhas, fluxos, tags, campos, motivos de perda, integrações, instâncias e demais usuários",
    icon: <RotateCcw className="w-5 h-5" />,
    color: "red",
  },
];

/** Ordem respeita FKs (ex.: scheduled_messages → templates/fluxos RESTRICT). */
const FULL_WIPE_TABLES = [
  "account_messages",
  "account_notifications",
  "account_notification_events",
  "account_contact_campaigns",
  "account_opportunities",
  "account_scheduled_messages",
  "account_contact_follow_up_states",
  "account_contact_interactions",
  "account_contacts",
  "account_campaigns",
  "account_pipeline_distribution",
  "account_pipeline_members",
  "account_pipeline_stages",
  "account_pipelines",
  "account_assistant_calendar_settings",
  "account_assistant_google_integrations",
  "account_assistant_webhooks",
  "account_assistant_follow_ups",
  "account_assistants",
  "account_templates",
  "account_fluxos",
  "account_tags",
  "account_fields",
  "account_loss_reasons",
  "account_config_distribute",
  "account_integrations",
  "account_google_integrations",
  "account_api_keys",
  "account_access_logs",
  "account_error_logs",
  "account_instances",
] as const;

async function deleteByAccountId(table: string, accountId: string) {
  if (!supabase) throw new Error(i18n.getFixedT(null, "settings")("reset.supabaseUnavailable"));

  const { error } = await supabase.from(table).delete().eq("account_id", accountId);
  if (error) throw error;
}

async function wipeAccountOperationalData(accountId: string) {
  await deleteByAccountId("account_messages", accountId);
  await deleteByAccountId("account_contact_campaigns", accountId);
  await deleteByAccountId("account_opportunities", accountId);
  await deleteByAccountId("account_scheduled_messages", accountId);
  await deleteByAccountId("account_contacts", accountId);
}

async function wipeEntireAccount(accountId: string, keepUserId: string) {
  if (!supabase) throw new Error(i18n.getFixedT(null, "settings")("reset.supabaseUnavailable"));

  for (const table of FULL_WIPE_TABLES) {
    await deleteByAccountId(table, accountId);
  }

  // Mantém o usuário logado para não perder acesso à conta
  const { error: usersError } = await supabase
    .from("account_users")
    .delete()
    .eq("account_id", accountId)
    .neq("id", keepUserId);
  if (usersError) throw usersError;

  // Não apaga account_notification_settings (INSERT é bloqueado por RLS).
  // Só restaura os toggles para o padrão.
  const { error: settingsError } = await supabase
    .from("account_notification_settings")
    .update({
      whatsapp_enabled: true,
      lead_assigned: true,
      lead_campaign_entered: true,
      calendar_ai_created: true,
      opportunity_pipeline_changed: true,
      opportunity_won: true,
      opportunity_lost: true,
      conversation_opened: true,
      conversation_closed: true,
      whatsapp_connected: true,
    })
    .eq("account_id", accountId);
  if (settingsError) throw settingsError;
}

const ResetTab: React.FC = () => {
  const { t } = useTranslation("settings");
  const { user } = useAuth();
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [counts, setCounts] = useState<Record<string, number | null>>({
    messages: null,
    crm: null,
    contacts: null,
    account: null,
  });

  useEffect(() => {
    if (!user?.account_id || !supabase) return;

    const sb = supabase;
    const fetchCounts = async () => {
      const [
        messagesRes,
        crmRes,
        contactsRes,
        assistantsRes,
        pipelinesRes,
        tagsRes,
        fieldsRes,
        fluxosRes,
        usersRes,
      ] = await Promise.all([
        sb
          .from("account_messages")
          .select("id", { count: "exact", head: true })
          .eq("account_id", user.account_id),
        sb
          .from("account_opportunities")
          .select("id", { count: "exact", head: true })
          .eq("account_id", user.account_id),
        sb
          .from("account_contacts")
          .select("id", { count: "exact", head: true })
          .eq("account_id", user.account_id),
        sb
          .from("account_assistants")
          .select("id", { count: "exact", head: true })
          .eq("account_id", user.account_id),
        sb
          .from("account_pipelines")
          .select("id", { count: "exact", head: true })
          .eq("account_id", user.account_id),
        sb
          .from("account_tags")
          .select("id", { count: "exact", head: true })
          .eq("account_id", user.account_id),
        sb
          .from("account_fields")
          .select("id", { count: "exact", head: true })
          .eq("account_id", user.account_id),
        sb
          .from("account_fluxos")
          .select("id", { count: "exact", head: true })
          .eq("account_id", user.account_id),
        sb
          .from("account_users")
          .select("id", { count: "exact", head: true })
          .eq("account_id", user.account_id),
      ]);

      const messages = messagesRes.count ?? 0;
      const crm = crmRes.count ?? 0;
      const contacts = contactsRes.count ?? 0;

      setCounts({
        messages,
        crm,
        contacts,
        account:
          messages +
          crm +
          contacts +
          (assistantsRes.count ?? 0) +
          (pipelinesRes.count ?? 0) +
          (tagsRes.count ?? 0) +
          (fieldsRes.count ?? 0) +
          (fluxosRes.count ?? 0) +
          (usersRes.count ?? 0),
      });
    };

    fetchCounts();
  }, [user?.account_id, result]);

  const handleReset = async (action: ResetAction) => {
    if (!user?.account_id || !user?.id || !supabase) return;
    if (confirmText !== "Apagar") return;

    setLoading(true);
    setResult(null);

    try {
      if (action.id === "account") {
        await wipeEntireAccount(user.account_id, user.id);
      } else if (action.id === "contacts") {
        await wipeAccountOperationalData(user.account_id);
      } else if (action.id === "crm") {
        const { error } = await supabase
          .from("account_opportunities")
          .delete()
          .eq("account_id", user.account_id);

        if (error) throw error;
      } else if (action.id === "messages") {
        const { error } = await supabase
          .from("account_messages")
          .delete()
          .eq("account_id", user.account_id);

        if (error) throw error;
      }

      setResult({
        type: "success",
        text:
          action.id === "account"
            ? "Conta zerada com sucesso."
            : t("lists.deleted", { label: t(action.labelKey) }),
      });
      setActiveAction(null);
      setConfirmText("");
    } catch (error: any) {
      console.error("Erro ao apagar dados:", error);
      setResult({ type: "error", text: `Erro: ${error.message || "Falha ao apagar dados"}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#FEF2F2] border border-red-200 rounded-xl flex items-center justify-center dark:bg-red-500/10 dark:border-red-500/20">
          <AlertTriangle className="w-5 h-5 text-[#DC2626] dark:text-[#F87171]" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("reset.alert")}</h3>
          <p className="text-sm text-[#64748B] dark:text-[#71717A]">
            {t("reset.alertBody")}
          </p>
        </div>
      </div>

      {result && (
        <div
          className={`p-3 rounded-lg mb-6 ${
            result.type === "success"
              ? "bg-[#ECFDF5] border border-green-200 text-[#16A34A] dark:bg-green-500/10 dark:border-green-500/20 dark:text-[#22C55E]"
              : "bg-[#FEF2F2] border border-red-200 text-[#DC2626] dark:bg-red-500/10 dark:border-red-500/20 dark:text-[#F87171]"
          }`}
        >
          {result.text}
        </div>
      )}

      <div className="space-y-4">
        {resetActions.map((action) => (
          <SettingsCard key={action.id}>
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="text-[#64748B] dark:text-[#71717A]">{action.icon}</div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-[#334155] dark:text-[#D4D4D8]">{t(action.labelKey)}</p>
                    {counts[action.id] !== null && (
                      <span className="text-xs font-mono bg-[#F1F5F9] dark:bg-[#27272A] text-[#475569] dark:text-[#A1A1AA] px-1.5 py-0.5 rounded">
                        {counts[action.id]?.toLocaleString("pt-BR")}{" "}
                        {counts[action.id] === 1 ? "item" : "itens"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#64748B] dark:text-[#71717A]">{t(action.descriptionKey)}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-red-500/30 text-[#DC2626] dark:text-[#F87171] hover:bg-red-500/10 hover:text-[#B91C1C] dark:hover:text-[#F87171]"
                onClick={() => {
                  setActiveAction(activeAction === action.id ? null : action.id);
                  setConfirmText("");
                  setResult(null);
                }}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {t("reset.erase")}
              </Button>
            </div>

            {activeAction === action.id && (
              <div className="border-t border-[#E5E7EB] p-4 bg-[#FEF2F2] dark:border-[#3F3F46] dark:bg-red-500/5">
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-[#DC2626] dark:text-[#F87171] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-[#DC2626] dark:text-[#F87171]">
                    <Trans
                      ns="settings"
                      i18nKey={
                        action.id === "account"
                          ? "reset.accountWarning"
                          : "reset.genericWarning"
                      }
                      components={{ b: <strong /> }}
                    />
                    <code className="bg-[#F1F5F9] dark:bg-[#27272A] px-1.5 py-0.5 rounded text-[#DC2626] dark:text-[#FCA5A5] font-mono text-xs">
                      {t("reset.erase")}
                    </code>{" "}
                    {t("reset.toConfirm")}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder='Digite "Apagar" para confirmar'
                    className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                  />
                  <Button
                    disabled={confirmText !== "Apagar" || loading}
                    onClick={() => handleReset(action)}
                    className="rounded-xl bg-[#DC2626] hover:bg-[#B91C1C] text-white disabled:opacity-30 min-w-[100px] transition-colors duration-200"
                  >
                    {loading ? "Apagando..." : "Confirmar"}
                  </Button>
                </div>
              </div>
            )}
          </SettingsCard>
        ))}
      </div>
    </div>
  );
};

export default ResetTab;
